import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ViewQueryResult } from '@salescore/core'
import merge from 'lodash.merge'

export interface ViewQueryResultsState {
  viewQueryResults: Record<string, ViewQueryResult> // viewId -> ViewQueryResult
}

const initialState: ViewQueryResultsState = {
  viewQueryResults: {},
}

// ViewQueryResult の派生物はこれまで Atom で管理されていましたが、 Redux の Slice で管理するように変更しました。
// 本来は Redux でなく以下のようにして ApolloClient のキャッシュで保持すべき内容と思います。
// 1. ViewQueryResult を scalarType から objectType にして、そのフラグメントを定義する
//   - 後方互換性を確保しながら、徐々に objectType の ViewQueryResult を用いるクライアントが増えるのを待つ必要があります
//   - この段階が一番の難しいため、 Recoil 脱却の取り組みとしては、 Redux に状態管理を移行することにしました
// 2. ViewQueryResult のフラグメントのキャッシュの ID を viewId にする
// 3. ViewQueryResult のフラグメントの各キャッシュフィールドについてカスタムの read, merge 関数を定義する
export const viewQueryResultsSlice = createSlice({
  name: 'viewQueryResults',
  initialState,
  reducers: {
    updateViewQueryResult: (state, action: PayloadAction<{ viewId: string; incoming: ViewQueryResult }>) => {
      const { viewId, incoming } = action.payload
      // eslint-disable-next-line no-param-reassign
      state.viewQueryResults[viewId] = {
        ...incoming,
        snapshots: merge(state.viewQueryResults[viewId]?.snapshots ?? {}, incoming.snapshots),
      }
    },
  },
  selectors: {
    _selectViewQueryResults: (state: ViewQueryResultsState) => state.viewQueryResults,
  },
})

export const selectViewQueryResult = createSelector(
  [viewQueryResultsSlice.selectors._selectViewQueryResults, (_, { viewId }: { viewId: string }) => viewId],
  (viewQueryResults, viewId) => viewQueryResults[viewId],
)

export const { updateViewQueryResult } = viewQueryResultsSlice.actions
export default viewQueryResultsSlice.reducer
